// Safe writes to contracts/{id}.
//
// A fianza lives INSIDE an array on the contract document, so every edit is a
// read-modify-write of the whole `fianzas[]` array. Doing that with getDoc →
// updateDoc loses concurrent writes: two brokers endorsing different fianzas on
// the same contract, or one endorsing while another marks a factura pagada, and
// whoever writes second silently erases the other's change. The money is not
// even the worst part — the endoso event itself disappears from eventos[].
//
// These helpers wrap the pattern in a real Firestore transaction, which re-reads
// and retries on conflict. Use them for ANY write that touches fianzas[] or
// documentos[]; never hand-roll getDoc → updateDoc on those arrays again.
//
// The money-moving endosos take a different route entirely: backend-nyx writes
// the fianza mutation and the comisión ledger movement in one server-side
// transaction, so the fianza and its money can never diverge.

import { doc, runTransaction } from 'firebase/firestore';

/**
 * Transactionally replace one fianza inside a contract.
 *
 * @param {Firestore} db
 * @param {string} contratoId
 * @param {string} fianzaId
 * @param {(fianza: object, ctx: {contrato: object, fianzas: object[], index: number}) => object|null} mutate
 *        Receives the CURRENT fianza (re-read inside the transaction, never the
 *        stale copy the component is rendering) and returns the replacement.
 *        Return null to abort without writing.
 * @param {object} [extraFields] Additional top-level contract fields to set in
 *        the same transaction (e.g. documentos[]), so they cannot drift apart.
 * @returns {Promise<object|null>} The fianza as written, or null if aborted.
 */
export async function mutateFianza(db, contratoId, fianzaId, mutate, extraFields = null) {
  return runTransaction(db, async (tx) => {
    const ref = doc(db, 'contracts', contratoId);
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error('Contrato no encontrado.');

    const contrato = snap.data();
    const fianzas = [...(contrato.fianzas || [])];
    const index = fianzas.findIndex((f) => f.id === fianzaId);
    if (index === -1) throw new Error('Fianza no encontrada en el contrato.');

    const next = mutate(fianzas[index], { contrato, fianzas, index });
    if (next == null) return null;

    fianzas[index] = next;
    tx.update(ref, { ...(extraFields || {}), fianzas, updatedAt: Date.now() });
    return next;
  });
}

/**
 * Transactionally rewrite arbitrary contract fields from their current value.
 * For edits that are not scoped to a single fianza — appending a fianza,
 * touching documentos[] and fianzas[] together, updating fianzasRequeridas.
 *
 * @param {(contrato: object) => object|null} mutate Returns the field map to
 *        write (merged, not replacing the document), or null to abort.
 */
export async function mutateContract(db, contratoId, mutate) {
  return runTransaction(db, async (tx) => {
    const ref = doc(db, 'contracts', contratoId);
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error('Contrato no encontrado.');

    const updates = mutate(snap.data());
    if (updates == null) return null;

    tx.update(ref, { ...updates, updatedAt: Date.now() });
    return updates;
  });
}
